import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Award, GraduationCap, BadgeCheck, BookOpen, Mail } from "lucide-react";
import { EMAIL } from "@/data/content";
import { api } from "@/lib/api";
import { imgUrl } from "@/lib/api";

const initials = (n) => n.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export default function DocenteDetail() {
  const { id } = useParams();
  const [d, setD] = React.useState(undefined);
  React.useEffect(() => { api.get(`/teachers/${id}`).then((r) => setD(r.data)).catch(() => setD(null)); }, [id]);
  if (d === undefined) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-gray-400">Caricamento…</div>;
  if (!d) return <div className="max-w-3xl mx-auto px-4 py-32 text-center"><p className="text-gray-500">Docente non trovato.</p><Link to="/docenti" className="text-[#EA6A0A] font-600">← Torna ai docenti</Link></div>;
  const mailto = `mailto:${EMAIL}?subject=${encodeURIComponent("Richiesta informazioni docente: " + d.name)}`;

  return (
    <div className="bg-white max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <Link to="/docenti" data-testid="docente-back" className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-600 mb-8 hover:text-[#EA6A0A] transition-colors"><ArrowLeft size={16} /> Tutti i docenti</Link>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="md:col-span-1">
          <div className="rounded-3xl overflow-hidden border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
            {d.photo ? (
              <img src={imgUrl(d.photo)} alt={d.name} loading="lazy" className="w-full h-80 object-cover object-top" data-testid="docente-photo" />
            ) : (
              <div className="w-full h-80 flex items-center justify-center" style={{ background: "linear-gradient(135deg,#EA6A0A 0%,#F59E42 100%)" }} data-testid="docente-photo">
                <span className="font-display font-800 text-7xl text-white/95">{initials(d.name)}</span>
              </div>
            )}
          </div>
        </motion.div>

        <div className="md:col-span-2">
          <h1 className="font-display font-800 tracking-tight text-3xl sm:text-4xl text-[#EA6A0A]">{d.name}</h1>
          <p className="text-base font-600 text-[#2FA84F] mt-1">{d.role}</p>
          <p className="mt-5 text-gray-600 leading-relaxed">{d.bio}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {d.specializzazioni.map((s) => (
              <span key={s} className="text-xs font-600 px-3 py-1.5 rounded-full bg-[#EA6A0A]/5 text-[#EA6A0A]">{s}</span>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 text-[#EA6A0A] mb-3"><BookOpen size={18} /><h3 className="font-display font-700 text-base">Corsi che tiene</h3></div>
              <ul className="space-y-2">{d.corsi.map((c) => <li key={c} className="text-sm text-gray-600 flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#2FA84F] mt-1.5 shrink-0" />{c}</li>)}</ul>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[#EA6A0A] mb-3"><BadgeCheck size={18} /><h3 className="font-display font-700 text-base">Certificazioni</h3></div>
              <ul className="space-y-2">{d.certificazioni.map((c) => <li key={c} className="text-sm text-gray-600 flex items-start gap-2"><BadgeCheck size={14} className="text-[#2FA84F] mt-0.5 shrink-0" />{c}</li>)}</ul>
            </div>
          </div>

          <a href={mailto} data-testid="docente-cta" className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#EA6A0A] text-white font-600 shadow-[0_8px_24px_-6px_rgba(0,51,102,0.4)] hover:bg-[#002244] hover:scale-[1.02] transition-all">
            <Mail size={17} /> Contatta il docente
          </a>
        </div>
      </div>
    </div>
  );
}
